/*eslint-disable*/
import { Suspense, lazy } from 'react';
import type { PartialRouteObject } from 'react-router';
import { Navigate } from 'react-router-dom';
import AuthGuard from './components/AuthGuard';
import FaqLayout from './components/faq/FaqLayout';
import BrowseLayout from './components/BrowseLayout';
import DashboardLayout from './components/dashboard/DashboardLayout';
import DocsLayout from './components/docs/DocsLayout';
import GuestGuard from './components/GuestGuard';
import LoadingScreen from './components/LoadingScreen';
import MainLayout from './components/MainLayout';
import OverviewBalanceGeneral from './pages/dashboard/OverviewBalanceGeneral';
import OverviewEstadoResultados from './pages/dashboard/OverviewEstadoResultados';
import OverviewPresupuestoEfectivo from './pages/dashboard/OverviewPresupuestoEfectivo';
import OverviewEstadoFlujoEfectivo from './pages/dashboard/OverviewEstadoFlujoEfectivo';
import OverviewIndicadoresFinancieros from './pages/dashboard/OverviewIndicadoresFinancieros';
import OverviewDetalleIndicadoresFinancieros from './pages/dashboard/OverviewDetalleIndicadoresFinancieros';

// import SettingsDrawer from './components/SettingsDrawer';

/* prettier-ignore */
const Loadable = (Component) => (props) => (
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);

// Browse pages

const Browse = Loadable(lazy(() => import('./pages/browse/Browse')));
const BrowseButtons = Loadable(
  lazy(() => import('./pages/browse/BrowseButtons'))
);
const BrowseCharts = Loadable(
  lazy(() => import('./pages/browse/BrowseCharts'))
);
const BrowseColors = Loadable(
  lazy(() => import('./pages/browse/BrowseColors'))
);
const BrowseDetailLists = Loadable(
  lazy(() => import('./pages/browse/BrowseDetailLists'))
);
const BrowseForms = Loadable(lazy(() => import('./pages/browse/BrowseForms')));
const BrowseGridLists = Loadable(
  lazy(() => import('./pages/browse/BrowseGridLists'))
);
const BrowseGroupedLists = Loadable(
  lazy(() => import('./pages/browse/BrowseGroupedLists'))
);
const BrowseInputs = Loadable(
  lazy(() => import('./pages/browse/BrowseInputs'))
);
const BrowseModals = Loadable(
  lazy(() => import('./pages/browse/BrowseModals'))
);
const BrowseQuickStats = Loadable(
  lazy(() => import('./pages/browse/BrowseQuickStats'))
);
const BrowseTables = Loadable(
  lazy(() => import('./pages/browse/BrowseTables'))
);
const BrowseTypography = Loadable(
  lazy(() => import('./pages/browse/BrowseTypography'))
);

// Authentication pages

const Login = Loadable(lazy(() => import('./pages/authentication/Login')));
const PasswordRecovery = Loadable(
  lazy(() => import('./pages/authentication/PasswordRecovery'))
);
const PasswordUpdateUser = Loadable(
  lazy(() => import('./pages/authentication/PasswordUpdateUser'))
);
const PasswordUpdateProvider = Loadable(
  lazy(() => import('./pages/authentication/PasswordUpdateProvider'))
);
const PasswordReset = Loadable(
  lazy(() => import('./pages/authentication/PasswordReset'))
);
const Register = Loadable(
  lazy(() => import('./pages/authentication/Register'))
);
const VerifyCode = Loadable(
  lazy(() => import('./pages/authentication/VerifyCode'))
);

// Faq page

const FaqList = Loadable(lazy(() => import('./pages/faq/FaqList')));

// Dashboard pages

const Account = Loadable(lazy(() => import('./pages/dashboard/Account')));
const Accounting = Loadable(lazy(() => import('./pages/dashboard/Accounting')));
const Contract = Loadable(lazy(() => import('./pages/dashboard/Contract')));
const Finance = Loadable(lazy(() => import('./pages/dashboard/Finance')));
const Glossary = Loadable(lazy(() => import('./pages/dashboard/Glossary')));
const Overview = Loadable(lazy(() => import('./pages/dashboard/Overview')));
const Settings = Loadable(lazy(() => import('./pages/dashboard/Settings')));
const Help = Loadable(lazy(() => import('./pages/dashboard/HelpSection')));
const OnboardingComplete = Loadable(lazy(() => import('./pages/OnboardingComplete')));

// Docs pages

const Docs = Loadable(lazy(() => import('./pages/Docs')));

// Error pages

const AuthorizationRequired = Loadable(
  lazy(() => import('./pages/AuthorizationRequired'))
);
const NotFound = Loadable(lazy(() => import('./pages/NotFound')));
const ServerError = Loadable(lazy(() => import('./pages/ServerError')));

// Other pages

const Home = Loadable(lazy(() => import('./pages/Home')));
const Politics = Loadable(lazy(() => import('./pages/Politics')));
const Conditions = Loadable(lazy(() => import('./pages/Conditions')));
const ContractView = Loadable(lazy(() => import('./pages/Contract')));

const routes: PartialRouteObject[] = [
  { path: '/contract', element: <ContractView /> },
  {
    path: '/reset',
    children: [
      {
        path: ':resetToken',
        element: <PasswordRecovery />,
      },
    ],
  },
  {
    path: '/update-password',
    children: [
      {
        path: ':updateToken',
        element: <PasswordUpdateProvider />,
      },
    ],
  },
  {
    path: '/update-password-user',
    children: [
      {
        path: ':updateToken',
        element: <PasswordUpdateUser />,
      },
    ],
  },
  {
    path: 'authentication',
    children: [
      {
        path: 'login',
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ),
      },
      {
        path: 'login-unguarded',
        element: <Login />,
      },
      {
        path: 'password-reset',
        element: <PasswordReset />,
      },
      {
        path: 'register',
        element: (
          <GuestGuard>
            <Register />
          </GuestGuard>
        ),
      },
      {
        path: 'register-unguarded',
        element: <Register />,
      },
      {
        path: 'verify-code',
        element: <VerifyCode />,
      },
    ],
  },
  {
    path: 'faq',
    element: <FaqLayout />,
    children: [
      {
        path: '/',
        element: <FaqList />,
      },
    ],
  },
  {
    path: 'complete_onboarding',
    element: <OnboardingComplete />,
  },
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout />
        {/* <SettingsDrawer /> */}
      </AuthGuard>
    ),
    children: [
      {
        path: '/',
        element: <Overview />,
      },
      {
        path: 'balance_general',
        element: <OverviewBalanceGeneral />,
      },
      {
        path: 'estado_resultados',
        element: <OverviewEstadoResultados />,
      },
      {
        path: 'presupuesto_efectivo',
        element: <OverviewPresupuestoEfectivo />,
      },
      {
        path: 'estado_Flujo',
        element: <OverviewEstadoFlujoEfectivo />,
      },
      {
        path: 'indicadores_financieros',
        element: <OverviewIndicadoresFinancieros />,
      },
      {
        path: 'detalle_indicadores_financieros',
        element: <OverviewDetalleIndicadoresFinancieros />,
      },
      {
        path: 'account',
        element: <Account />,
      },
      {
        path: 'glossary',
        element: <Glossary />,
      },
      {
        path: 'settings',
        element: <Settings />,
      },
      {
        path: 'help',
        element: <Help />,
      },
      {
        path: 'contract',
        element: <Contract />,
      },
      {
        path: 'accounting',
        element: <Accounting />,
      },
      {
        path: 'finance',
        element: <Finance />,
      },
    ],
  },
  {
    path: 'docs',
    element: <DocsLayout />,
    children: [
      {
        path: '/',
        element: <Navigate to="/docs/overview/welcome" replace />,
      },
      {
        path: '*',
        element: <Docs />,
      },
    ],
  },
  {
    path: '*',
    element: (<MainLayout />),
    // element: (
    //     <GuestGuard>
    //       <Login />
    //     </GuestGuard>
    // ),
    children: [
      {
        path: '/',
        element: <Home />,
      },
      {
        path: '/condiciones',
        element: <Conditions />,
      },
      {
        path: '/politicas',
        element: <Politics />,
      },
      {
        path: 'browse',
        element: <BrowseLayout />,
        children: [
          {
            path: '/',
            element: <Browse />,
          },
          {
            path: '/buttons',
            element: <BrowseButtons />,
          },
          {
            path: '/inputs',
            element: <BrowseInputs />,
          },
          {
            path: '/charts',
            element: <BrowseCharts />,
          },
          {
            path: '/colors',
            element: <BrowseColors />,
          },
          {
            path: '/data-display/detail-lists',
            element: <BrowseDetailLists />,
          },
          {
            path: '/data-display/quick-stats',
            element: <BrowseQuickStats />,
          },
          {
            path: '/data-display/tables',
            element: <BrowseTables />,
          },
          {
            path: '/forms',
            element: <BrowseForms />,
          },
          {
            path: '/modals',
            element: <BrowseModals />,
          },
          {
            path: '/lists/grouped-lists',
            element: <BrowseGroupedLists />,
          },
          {
            path: '/lists/grid-lists',
            element: <BrowseGridLists />,
          },
          {
            path: '/typography',
            element: <BrowseTypography />,
          },
        ],
      },
      {
        path: '401',
        element: <AuthorizationRequired />,
      },
      {
        path: '404',
        element: <NotFound />,
      },
      {
        path: '500',
        element: <ServerError />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
];

export default routes;
